import { createUmi } from '@metaplex-foundation/umi-bundle-defaults'
import { backendConnection, connection } from '../config/config'
import { dasApi } from '@metaplex-foundation/digital-asset-standard-api'
import { toWeb3JsInstruction } from '@metaplex-foundation/umi-web3js-adapters'
import { PublicKey } from '@solana/web3.js'
import { fetchAllDigitalAssetByOwner } from 'newMPLMetadata'
import { createNoopSigner, publicKey } from '@metaplex-foundation/umi'
import { fetchAsset, fetchAssetsByOwner, transferV1 } from '@metaplex-foundation/mpl-core'
import axios from 'axios'

export const umi = createUmi(backendConnection).use(dasApi())

export async function fetchAllNFTsByOwner(owner: string) {
  const nfts = await umi.rpc.getAssetsByOwner({
    sortBy: {
      sortBy: 'recent_action',
      sortDirection: 'desc',
    },
    limit: 100,
    owner: publicKey(owner),
  })

  return nfts
}

export async function transferCoreNFTInstructions({
  mint,
  from,
  to,
  payer,
}: {
  mint: string
  from: string
  to: string
  payer?: string
}) {
  const asset = await fetchSingleCoreNFT(mint)
  const fromSigner = createNoopSigner(publicKey(from))
  const payerSigner = createNoopSigner(publicKey(payer ?? from))

  const instructions = transferV1(umi, {
    asset: publicKey(mint),
    newOwner: publicKey(to),
    authority: fromSigner,
    payer: payerSigner,
    collection:
      asset.updateAuthority.type === 'Collection' && !!asset.updateAuthority.address
        ? publicKey(asset.updateAuthority.address)
        : undefined,
  }).getInstructions()

  return instructions.map((i) => toWeb3JsInstruction(i))
}

export async function fetchCoreAssetWithNFTMetadata(nftMint: string) {
  const nft = await fetchSingleCoreNFT(nftMint)
  const metadataRes = await axios.get(nft.uri)

  return { asset: nft, metadata: metadataRes.data }
}

export async function fetchSingleCoreNFT(nftMint: string) {
  const nft = await fetchAsset(umi, nftMint, { skipDerivePlugins: false })
  return nft
}

export async function fetchAllCoreNFTsByOwner(owner: string): Promise<{ publicKey: any; name: string; uri: string; owner: any }[]> {
  try {
    // Use DAS API instead of on-chain getProgramAccounts to avoid Helius 5M account limit.
    const dasAssets = await umi.rpc.getAssetsByOwner({
      owner: publicKey(owner),
      limit: 1000,
    })

    const coreAssets = dasAssets.items
      .filter((asset: any) => {
        const iface = asset.interface
        return iface === 'MplCoreAsset' || iface === 'MplCore'
      })
      .map((asset: any) => ({
        publicKey: asset.id,
        name: (asset.content?.metadata?.name ?? '') as string,
        uri: (asset.content?.json_uri ?? '') as string,
        owner: publicKey(owner),
      }))

    console.log(`[fetchAllCoreNFTsByOwner] DAS returned ${dasAssets.items.length} total, ${coreAssets.length} core assets`)

    return coreAssets
  } catch (e) {
    console.warn('Failed to fetch core NFTs via DAS, falling back to on-chain', e)
    const assetsByOwner = await fetchAssetsByOwner(umi, owner, {
      skipDerivePlugins: false,
    })
    return assetsByOwner.map((a) => ({
      publicKey: a.publicKey,
      name: a.name,
      uri: a.uri,
      owner: a.owner,
    }))
  }
}
